import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Trash2, Edit2, Save, X, AlertCircle,
  Package, Wrench, Gauge, Ruler, CircleDot, Stethoscope,
  Wind, Drill, Scissors, Hammer, CheckCircle, Zap,
  Cog, TrendingUp, Archive, Disc, Paintbrush, Flame,
  Layers, Sparkles, Shield, Droplet, MoreHorizontal
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

// Map pour convertir nom → composant icon
const IconMap = {
  Package, Wrench, Gauge, Ruler, CircleDot, Stethoscope,
  Wind, Drill, Scissors, Hammer, CheckCircle, Zap,
  Cog, TrendingUp, Archive, Disc, Paintbrush, Flame,
  Layers, Sparkles, Shield, Droplet, MoreHorizontal
};

// Composant pour afficher une icône
const IconDisplay = ({ iconName, className = "w-5 h-5" }) => {
  const IconComponent = IconMap[iconName] || Package;
  return <IconComponent className={className} />;
};

const GestionCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    icon: 'Package',
    couleur: '#3B82F6'
  });

  // Icônes disponibles
  const iconsDisponibles = [
    'Package', 'Wrench', 'Gauge', 'Ruler', 'CircleDot', 'Stethoscope',
    'Wind', 'Drill', 'Scissors', 'Hammer', 'CheckCircle', 'Zap',
    'Cog', 'TrendingUp', 'Archive', 'Disc', 'Paintbrush', 'Flame',
    'Layers', 'Sparkles', 'Shield', 'Droplet', 'MoreHorizontal'
  ];

  // Couleurs prédéfinies
  const couleursPredefines = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#EC4899', '#F97316', '#84CC16', '#6366F1',
    '#14B8A6', '#DC2626', '#9CA3AF', '#6B7280'
  ];

  useEffect(() => {
    chargerCategories();
  }, []);

  const chargerCategories = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mat_categories')
        .select('*')
        .order('nom');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
      alert('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Modification
        const { error } = await supabase
          .from('mat_categories')
          .update({
            nom: formData.nom,
            icon: formData.icon,
            couleur: formData.couleur
          })
          .eq('id', editingId);

        if (error) throw error;
        alert('Catégorie modifiée avec succès !');
      } else {
        // Création
        const { error } = await supabase
          .from('mat_categories')
          .insert([{
            nom: formData.nom,
            icon: formData.icon,
            couleur: formData.couleur
          }]);

        if (error) throw error;
        alert('Catégorie créée avec succès !');
      }

      // Réinitialiser
      setFormData({ nom: '', icon: 'Package', couleur: '#3B82F6' });
      setShowForm(false);
      setEditingId(null);
      chargerCategories();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleEdit = (categorie) => {
    setFormData({
      nom: categorie.nom,
      icon: categorie.icon,
      couleur: categorie.couleur
    });
    setEditingId(categorie.id);
    setShowForm(true);
  };

  const handleDelete = async (id, nom) => {
    // Vérifier si des outils utilisent cette catégorie
    const { count } = await supabase
      .from('mat_outils')
      .select('id', { count: 'exact', head: true })
      .eq('categorie_id', id);

    if (count > 0) {
      alert(`Impossible de supprimer "${nom}" : ${count} outil(s) utilisent cette catégorie.`);
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir supprimer la catégorie "${nom}" ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('mat_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Catégorie supprimée avec succès !');
      chargerCategories();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur: ' + error.message);
    }
  };

  const handleCancel = () => {
    setFormData({ nom: '', icon: 'Package', couleur: '#3B82F6' });
    setShowForm(false);
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Catégories</h2>
          <p className="text-gray-400 mt-1">{categories.length} catégorie(s)</p>
        </div>
        
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nouvelle catégorie
          </button>
        )}
      </div>

      {/* Formulaire */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 rounded-lg p-6 border border-slate-700"
        >
          <h3 className="text-xl font-semibold text-white mb-4">
            {editingId ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom de la catégorie *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Appareils de diagnostic"
                required
              />
            </div>

            {/* Icône */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Icône
              </label>
              <div className="grid grid-cols-8 gap-2">
                {iconsDisponibles.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      formData.icon === icon
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-slate-600 bg-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <IconDisplay iconName={icon} className="w-5 h-5 mx-auto" />
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Icône sélectionnée : {formData.icon}</p>
            </div>

            {/* Couleur */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Couleur
              </label>
              <div className="grid grid-cols-7 gap-2">
                {couleursPredefines.map((couleur) => (
                  <button
                    key={couleur}
                    type="button"
                    onClick={() => setFormData({ ...formData, couleur })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      formData.couleur === couleur
                        ? 'border-white scale-110'
                        : 'border-slate-600 hover:scale-105'
                    }`}
                    style={{ backgroundColor: couleur }}
                  />
                ))}
              </div>
            </div>

            {/* Aperçu */}
            <div className="bg-slate-700 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-2">Aperçu :</p>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: formData.couleur }}
              >
                <IconDisplay iconName={formData.icon} />
                <span>{formData.nom || 'Nom de la catégorie'}</span>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingId ? 'Modifier' : 'Créer'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-2 px-6 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
              >
                <X className="w-4 h-4" />
                Annuler
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Liste des catégories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((categorie) => (
          <motion.div
            key={categorie.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-slate-600 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: categorie.couleur }}
                >
                  <IconDisplay iconName={categorie.icon} />
                </div>
                <div>
                  <h3 className="text-white font-medium">{categorie.nom}</h3>
                  <p className="text-sm text-gray-400">{categorie.icon}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(categorie)}
                  className="p-2 text-blue-400 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Modifier"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(categorie.id, categorie.nom)}
                  className="p-2 text-red-400 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune catégorie. Créez-en une pour commencer !</p>
        </div>
      )}
    </div>
  );
};

export default GestionCategories;